import type { LucideIcon } from "lucide-react";

/**
 * An honest empty state.
 *
 * Every destination in the app exists from M1.2 onward, but almost none of them have
 * anything behind them yet. The rule this component exists to enforce is that an empty
 * room says so and names the milestone that furnishes it — no fake rows, no invented
 * counts, no spinner that will never resolve. Somebody navigating the shell should
 * always be able to tell "nothing here yet" from "something is broken".
 */
export function Placeholder({
  icon: Icon,
  title,
  summary,
  milestone,
  children,
}: {
  icon: LucideIcon;
  title: string;
  summary: string;
  /** The milestone that fills this screen, e.g. "M5.2". */
  milestone: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="px-[var(--space-8)] py-[var(--space-8)]">
      <header className="mb-[var(--space-8)]">
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          {title}
        </h1>
        <p className="mt-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">{summary}</p>
      </header>

      <div className="flex flex-col items-center justify-center rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] bg-[var(--surface-glass)] px-[var(--space-8)] py-[var(--space-12)] text-center backdrop-blur-[var(--blur-sm)]">
        <div className="mb-[var(--space-4)] flex h-12 w-12 items-center justify-center rounded-[var(--radius-full)] bg-[var(--surface-2)]">
          <Icon size={22} strokeWidth={2} className="text-[var(--text-muted)]" />
        </div>

        <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">Nothing here yet</p>

        <p className="mt-1 max-w-[46ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          This screen is built in{" "}
          <span className="font-semibold text-[var(--text-primary)]">{milestone}</span>. The room exists so
          everything around it can be finished first — the shell, the routes and both themes are real.
        </p>

        {children}
      </div>
    </div>
  );
}
